import { lazy } from 'react'
import { safelyParseJSON } from 'utils/safelyParseJSON'
import { reloadWithoutCache } from 'utils/reloadWithoutCache'

// Vite's wording, not webpack's ChunkLoadError.
const CHUNK_ERROR_PATTERNS = [
  'ChunkLoadError',
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
]

const isChunkLoadError = (e: Error) => {
  const text = `${e?.name}: ${e?.message}`
  return CHUNK_ERROR_PATTERNS.some((p) => text.includes(p))
}

const lsKey = 'lazyLoadFailed'

// the latch stops a reload loop; it expires so a client isn't stuck forever
const LATCH_TTL_MS = 24 * 60 * 60 * 1000

const didLazyLoadFail = () => {
  const at = safelyParseJSON(localStorage.getItem(lsKey) || 'null')
  if (typeof at !== 'number') return false
  return Date.now() - at < LATCH_TTL_MS
}

const setLazyLoadFailed = () =>
  localStorage.setItem(lsKey, JSON.stringify(Date.now()))

// Another chunk succeeding does NOT mean the failed one recovered, and the app
// always loads several. Only clear once the latch has outlived a reload cycle,
// or a sibling success re-arms the loop on every attempt.
const SETTLED_MS = 30 * 1000

const clearLazyLoadFailedIfSettled = () => {
  const at = safelyParseJSON(localStorage.getItem(lsKey) || 'null')
  if (typeof at !== 'number' || Date.now() - at > SETTLED_MS) {
    localStorage.removeItem(lsKey)
  }
}

// Soft-reloads once for a stale chunk after a deploy. Returns false (and does
// nothing) for any other error, or when a reload already failed to fix it.
export const recoverFromChunkLoadError = (e: unknown) => {
  if (!isChunkLoadError(e as Error) || didLazyLoadFail()) return false
  setLazyLoadFailed()
  reloadWithoutCache()
  return true
}

// You can use this function to code-split out chunks of the application.
// Please *only* use this on *internal* sections of the application, that
// external users will *literally never need*. Otherwise, we run into the
// following pitfall:

//   1. user loads app
//   2. flowhub releases new version of the app
//   3. user (on the old version) navigates to a code-split portion of
//      the app whose js chunk no longer exists (we deployed over it).
export const lazyLoadComponent: typeof lazy = (importFn) =>
  // @ts-ignore
  lazy(async () => {
    try {
      const component = await importFn()
      clearLazyLoadFailedIfSettled()
      return component
    } catch (e) {
      console.error(e)
      // Only soft-reload once for missing chunks after a deploy; other import
      // failures should surface instead of thrashing the page.
      if (!recoverFromChunkLoadError(e)) throw e
      // hold Suspense until the reload lands; resolving blanks the screen
      return new Promise<never>(() => {})
    }
  })
